import { createAsyncThunk } from '@reduxjs/toolkit';
import { toastr } from 'react-redux-toastr';
import { registerFastStudent } from '@/api/studentsApi';
import {
  priceSanteMobiliteApi,
  downloadAttestationApi,
  isCountryNotCoveredError,
  CountryDto,
} from '@/api/santeMobiliteApi';
import { RootState } from '@/redux/reducers';
import { authSuccess } from '@/redux/actions/authActions';
import LocalStorageService from '@/services/LocalStorageService';
import { appError, appLoading, appSuccess } from '@/redux/actions/appActions';
import fileDownload from 'js-file-download';

interface SanteMobiliteAddress {
  street: string;
  postalCode: string;
  city: string;
  country: string;
}

interface AxiosLikeError {
  response?: { data?: { message?: string } };
}

const extractError = (err: unknown) => {
  const e = err as AxiosLikeError;
  return {
    message: e?.response?.data?.message,
    data: e?.response?.data,
  };
};

export const registerTempUserSanteMobilite = createAsyncThunk(
  'santeMobilite/registerTempUser',
  async (
    address: SanteMobiliteAddress | null,
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;
    const tempUser = state.user;
    const schoolName = state.santeMobilite.schoolName;

    const payload = {
      user: {
        name: tempUser.firstname,
        lastName: tempUser.lastname,
        email: tempUser.email,
        password: tempUser.password,
      },
      civility: tempUser.civility,
      address,
      phoneNumber: tempUser.phone,
      birthday: tempUser.birthdate,
      studentType: 'classic',
      referralCode: null,
      // Si un slug ?school=... a été détecté dans l'URL d'entrée, on l'envoie au
      // back qui rattachera le compte à l'école et appliquera le bon studentType
      // (inclusive/partner). Slug inconnu = back ignore et garde 'classic'.
      ...(schoolName ? { schoolName } : {}),
    };

    try {
      dispatch(appLoading());
      const response = await registerFastStudent(payload);
      const { token, refresh_token, data } = response.data;
      const service = LocalStorageService.getService();

      service.setToken({ token, refresh_token });
      service.setUser({ user: data });

      dispatch(authSuccess({ data: { user: data }, token }));
      dispatch(appSuccess());

      toastr.success(
        'Confirmation email envoyée',
        'Un email de confirmation vient de vous être envoyé.',
      );
      return response.data;
    } catch (error: unknown) {
      const { message, data } = extractError(error);
      dispatch(appError());
      toastr.error('Erreur', message || "Erreur lors de l'inscription");
      return rejectWithValue(data);
    }
  },
);

export const fetchSanteMobilitePrice = createAsyncThunk(
  'santeMobilite/fetchPrice',
  async (
    {
      zone,
      monthsCount,
      partnerLevelHealthMobilityPrice,
      hasFrenchSocialSecurity,
      schoolName,
    }: {
      zone: string;
      monthsCount: number;
      partnerLevelHealthMobilityPrice?: number;
      hasFrenchSocialSecurity?: boolean | null;
      schoolName?: string | null;
    },
    { getState, rejectWithValue, dispatch },
  ) => {
    try {
      dispatch(appLoading());
      // Fallback : si l'appelant ne précise pas explicitement schoolName, on
      // le récupère depuis le slice (capturé via `?school=...` à l'arrivée
      // sur le wizard). Permet à PriceHeadline d'envoyer le slug même
      // quand on est en fast register (pas encore connecté → pas de school
      // hydratée dans state.school).
      const state = getState() as RootState;
      const slug = schoolName ?? state.santeMobilite.schoolName ?? undefined;
      const response = await priceSanteMobiliteApi({
        zone,
        monthsCount,
        partnerLevelHealthMobilityPrice,
        hasFrenchSocialSecurity,
        schoolName: slug,
      });
      dispatch(appSuccess());
      return response.data?.data ?? response.data;
    } catch (error: unknown) {
      const { message, data } = extractError(error);
      dispatch(appError());
      // Cas spécifique zone 0 : pays non couvert. Le caller (FinalizationStep)
      // re-route l'utilisateur vers le step Destination plutôt que d'afficher un
      // toastr d'erreur générique.
      if (isCountryNotCoveredError(error)) {
        return rejectWithValue({ ...data, countryNotCovered: true });
      }
      toastr.error(
        'Erreur',
        message || 'Impossible de récupérer le tarif pour le moment.',
      );
      return rejectWithValue(data);
    }
  },
);

export const downloadSanteMobiliteAttestation = createAsyncThunk(
  'santeMobilite/downloadAttestation',
  async (
    params: { startDate: string; endDate: string; country: CountryDto },
    { rejectWithValue, dispatch },
  ) => {
    try {
      dispatch(appLoading());
      const response = await downloadAttestationApi(params);
      fileDownload(response.data, 'AttestationSanteMobilite.pdf');
      dispatch(appSuccess());
      // Identifiant du Document qui vient d'être écrit, lu dans l'en-tête de
      // réponse (exposé via nelmio_cors). Il permet à l'écran de remerciement
      // de RELIRE ce fichier par `GET /documents/{id}` au lieu de rappeler cet
      // endpoint — qui recréerait un contrat, un document et un email.
      //
      // C'est l'identifiant qui compte, pas la référence : le contrat créé ici
      // est de type DOCUMENT_CERTIFICATE, alors que `/attestation-info/{ref}`
      // ne cherche que les DOCUMENT_CONTRACT et répond 404. Vérifié en preprod
      // sur StudMH-2609120013-2254.
      //
      // Les en-têtes axios sont normalisés en minuscules.
      const attestationDocumentId: number | null = Number(
        response.headers?.['x-attestation-document-id'],
      ) || null;
      const contractReference: string | null =
        (response.headers?.['x-contract-reference'] as string) || null;
      toastr.success(
        'Attestation téléchargée',
        'Vous venez de recevoir votre attestation sur votre adresse mail et dans votre rubrique mes documents.',
      );
      return { attestationDocumentId, contractReference };
    } catch (error: unknown) {
      const { message, data } = extractError(error);
      dispatch(appError());
      toastr.error(
        'Erreur',
        message || "Impossible de télécharger l'attestation.",
      );
      return rejectWithValue(data);
    }
  },
);

